// The Teams review prompt (ADN-591). The approve endpoints count approvals the
// Teams app makes (see ./sharedClient) and answer with `X-Review-Prompt: due`
// when the prompt should open; everything else here reports what happened to it.
//
// The reports are plain calls on the shared web client rather than react-query
// mutations. The prompt's trigger waits for the mutation cache to go quiet and
// drops the prompt on a failed mutation; its own bookkeeping must not show up there.
import type { AxiosResponse } from 'axios'

import api from '@src/api/api'

export const REVIEW_PROMPT_HEADER = 'x-review-prompt'
export const REVIEW_PROMPT_DUE = 'due'

export const REVIEW_PROMPT_PATHS = {
  shown: '/api/my-account/teams-review-prompt/shown',
  dismissals: '/api/my-account/teams-review-prompt/dismissals',
  rating: '/api/my-account/teams-review-prompt/rating',
  feedback: '/api/my-account/teams-review-prompt/feedback',
  reviewClicks: '/api/my-account/teams-review-prompt/review-clicks',
}

const dueListeners = new Set<() => void>()

/** Called whenever an approval response says the prompt is due. Returns its unsubscribe function. */
export const subscribeToReviewPromptDue = (listener: () => void): (() => void) => {
  dueListeners.add(listener)
  return () => {
    dueListeners.delete(listener)
  }
}

export const readReviewPromptHeader = (response: AxiosResponse): AxiosResponse => {
  if (response.headers?.[REVIEW_PROMPT_HEADER] === REVIEW_PROMPT_DUE) {
    dueListeners.forEach((listener) => listener())
  }
  return response
}

api.interceptors.response.use(readReviewPromptHeader)

/** Pauses before each retry of `shown`. */
export const SHOWN_RETRY_DELAYS_MS = [1000, 3000]

const withRetry = async <T>(call: () => Promise<T>, delaysMs: number[]): Promise<T> => {
  try {
    return await call()
  } catch (err) {
    const [delay, ...rest] = delaysMs
    if (delay === undefined) throw err
    await new Promise((resolve) => setTimeout(resolve, delay))
    return withRetry(call, rest)
  }
}

// Retried: without it the backend never starts the 7-day wait or the cap, and
// the next approval would ask again.
export const markShown = () => withRetry(() => api.post(REVIEW_PROMPT_PATHS.shown), SHOWN_RETRY_DELAYS_MS)

export const dismiss = () => api.post(REVIEW_PROMPT_PATHS.dismissals)

export const submitRating = (rating: number) => api.post(REVIEW_PROMPT_PATHS.rating, { rating })

export const submitFeedback = (feedback: string) => api.post(REVIEW_PROMPT_PATHS.feedback, { feedback })

export const trackReviewClick = () => api.post(REVIEW_PROMPT_PATHS.reviewClicks)
